import { motion } from "framer-motion";

const pillars = [
  {
    label: "Creative",
    description:
      "Visual identity, packaging systems, and product design — crafted with editorial precision and real commercial awareness.",
  },
  {
    label: "Commercial",
    description:
      "Range architecture, positioning strategy, and pricing logic — designed to perform on shelf, online, and in pitch decks.",
  },
  {
    label: "Consultancy",
    description:
      "Insight-led planning, retail readiness audits, and brand strategy — structured for scale and real-world execution.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.15, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

export const C3Framework = () => {
  return (
    <section
      id="framework"
      data-testid="c3-framework-section"
      className="py-24 md:py-36 bg-deep-black text-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeUp}
          custom={0}
          className="mb-16 md:mb-24"
        >
          <span className="font-body text-xs uppercase tracking-[0.2em] text-white/40 block mb-6">
            Our Approach
          </span>
          <h2
            data-testid="c3-heading"
            className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-[-0.03em] font-light leading-[1.1] mb-6"
          >
            C<sup className="text-2xl sm:text-3xl align-super">3</sup> Framework
          </h2>
          <p className="font-body text-base md:text-lg text-white/50 max-w-xl leading-relaxed font-light">
            Every project runs through our C{"\u00B3"} model — a structured approach
            that balances creative ambition with commercial reality and
            consultancy rigour.
          </p>
        </motion.div>

        {/* Pillars */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {pillars.map((pillar, i) => (
            <motion.div
              key={pillar.label}
              data-testid={`c3-pillar-${pillar.label.toLowerCase()}`}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={fadeUp}
              custom={i + 1}
              className="border border-white/10 rounded-2xl p-8 md:p-10 hover:border-white/25 transition-all duration-500 group"
            >
              <div className="flex items-center gap-3 mb-6">
                <span className="w-2 h-2 rounded-full bg-white/40 group-hover:bg-white transition-colors duration-500" />
                <h3 className="font-heading text-xl md:text-2xl tracking-tight font-medium">
                  {pillar.label}
                </h3>
              </div>
              <p className="font-body text-sm text-white/50 leading-relaxed font-light group-hover:text-white/70 transition-colors duration-500">
                {pillar.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
